import React, { useState, useEffect } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import { useGetOrdersQuery } from "../../../features/order/orderSlice";
import { Order, OrderStatus } from "../../../features/order/types";
import { Loader } from "../../../shared/Loader";
import { OrdersTableSkeleton } from "./OrdersTableSkeleton";
import CopyableOrderId from "@/components/CopyableOrderId";

export const OrdersTablePanel = () => {
  const [tab, setTab] = useState<string | null>("sell");

  const [page, setPage] = useState<number>(1);
  const [searchParams] = useSearchParams();
  const pageFromUrl = searchParams.get("page");
  const typeFromUrl = searchParams.get("type");

  useEffect(() => {
    setPage(Number(pageFromUrl));
    setTab(typeFromUrl);
  }, [typeFromUrl]);

  const {
    data: orders,
    isLoading,
    isFetching,
    isError,
  } = useGetOrdersQuery({
    type: typeFromUrl as string,
    page: Number(pageFromUrl),
  });

  return (
    <div className="bg-black flex flex-col gap-4 px-4 lg:px-10 py-10">
      <div className="flex justify-between">
        <h3 className="text-primary text-xl font-medium">Recent Orders</h3>

        <div className="flex items-center gap-2 rounded-lg bg-gray-500 p-1">
          <Link to="/app/transaction/orders?type=sell&page=1">
            <button
              className={`py-2 px-4 ${
                tab === "sell" ? "bg-zinc-600" : "bg-transparent"
              }  rounded-lg text-gray-300 text-sm`}
              onClick={() => setTab("sell")}
            >
              Sell
            </button>
          </Link>

          <Link to="/app/transaction/orders?type=buy&page=1">
            <button
              className={`py-2 px-4 ${
                tab === "buy" ? "bg-zinc-600" : "bg-transparent"
              } rounded-lg text-gray-300 text-sm`}
              onClick={() => setTab("buy")}
            >
              Buy
            </button>
          </Link>
        </div>
      </div>

      <div className="overflow-x-auto relative">
        {isLoading || isFetching ? (
          <OrdersTableSkeleton />
        ) : orders?.data.length == 0 ? (
          <div className="w-full bg-red font-light text-white/40 my-24 text-center">
            No orders found
          </div>
        ) : (
          <table className="relative w-full text-sm text-left text-gray-400">
            <thead className="text-xs uppercase bg-gray-700 text-gray-300">
              <tr>
                <th scope="col" className="py-3 px-6">
                  Transaction ID
                </th>
                <th scope="col" className="py-3 px-6">
                  Type
                </th>

                <th scope="col" className="py-3 px-6">
                  Amount
                </th>
                <th scope="col" className="py-3 px-6">
                  Rate
                </th>
                <th scope="col" className="py-3 px-6">
                  Status
                </th>
                <th scope="col" className="py-3 px-6"></th>
              </tr>
            </thead>
            <tbody>
              {orders?.data?.map((data: Order) => (
                <tr className="border-b bg-transparent border-gray-700">
                  <th
                    scope="row"
                    className="py-4 px-6 font-medium whitespace-nowrap text-white"
                  >
                    <CopyableOrderId orderId={data._id as string} />
                  </th>
                  <td className="py-4 px-6">{data.type}</td>

                  <td className="py-4 px-6">{data.quantity}</td>
                  <td className="py-4 px-6">{data.price}</td>
                  <td className="py-4 px-6">
                    {data.status === OrderStatus.COMPLETED ? (
                      <div className="badge-success px-4 py-2 rounded-full">
                        Completed
                      </div>
                    ) : data.status === OrderStatus.PENDING ? (
                      <div className="badge-warning px-4 py-2 rounded-full">
                        Pending
                      </div>
                    ) : data.status === OrderStatus.PROCESSING ? (
                      <div className="badge-info px-4 py-2 rounded-full">
                        Processing
                      </div>
                    ) : data.status === OrderStatus.SUSPENDED ? (
                      <div className="badge-suspended px-4 py-2 rounded-full">
                        Suspended
                      </div>
                    ) : data.status === OrderStatus.CANCELED ? (
                      <div className="badge-error px-4 py-2 rounded-full">
                        Cancelled
                      </div>
                    ) : (
                      <div className="badge-info px-4 py-2 rounded-full">
                        {data.status}
                      </div>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <Link to={`/app/transaction/orders/${data._id}`}>
                      <button className="box-content text-blue-500 text-xs">
                        Details
                      </button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="flex items-center gap-5 mt-12 ml-6">
          <p className="text-white/60 font-light text-sm">
            page: {page} | total: {orders?.total} orders
          </p>
          <div className="flex items-center gap-2">
            <Link
              to={`/app/transaction/orders?type=${tab}&page=${page - 1 || 1}`}
            >
              <button
                className="rounded-full bg-gray-500 hover:bg-gray-600 text-white/70 disabled:cursor-not-allowed disabled:bg-gray-500/50 disabled:text-white/20 text-lg cursor-pointer"
                disabled={pageFromUrl === "1"}
                onClick={() => setPage(page - 1)}
              >
                <FiChevronLeft className="box-content p-3" />
              </button>
            </Link>
            <Link
              to={`/app/transaction/orders?type=${tab}&page=${
                page < orders?.last_page ? page + 1 : page
              }`}
            >
              <button
                className="rounded-full bg-gray-500 hover:bg-gray-600 text-white/70 disabled:cursor-not-allowed disabled:bg-gray-500/50 disabled:text-white/20 text-lg cursor-pointer"
                disabled={pageFromUrl == orders?.last_page}
                onClick={() => setPage(page + 1)}
              >
                <FiChevronRight className="box-content p-3" />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
